import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenantStore } from '../store/useTenantStore';
import { apiClient } from '../lib/api/client';
import { TenantAvailabilityResponse, ApiResponse } from '../lib/api/types';
import { ArrowRight, AlertCircle } from 'lucide-react';
import '../styles/animations.css';

export const OrgCodePage: React.FC = () => {
  const navigate = useNavigate();
  const setTenant = useTenantStore((state) => state.setTenant);
  const [orgCode, setOrgCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [focused, setFocused] = useState(false);

  const cleanOrgCode = (code: string): string => {
    return code
      .toLowerCase()
      .replace('.sinavza.com', '')
      .replace('.yks.istanbul', '')
      .trim();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    const cleanedCode = cleanOrgCode(orgCode);
    
    if (!cleanedCode) {
      setError('Lütfen kurum kodunu giriniz');
      return;
    }

    setLoading(true);

    try {
      const response = await apiClient.checkTenantAvailability(cleanedCode);
      const data = response.data as ApiResponse<TenantAvailabilityResponse>;

      if (data.success && data.result) {
        if (data.result.state === 1 && data.result.tenantId && data.result.serverRootAddress) {
          // Save tenant info
          setTenant(
            data.result.tenantId, 
            data.result.serverRootAddress,
            cleanedCode
          );
          
          // Navigate to login
          navigate('/login');
        } else if (data.result.state === 2) {
          setError('Bu kurum şu anda aktif değil.');
        } else {
          setError('Geçersiz kurum kodu. Lütfen kontrol ediniz.');
        }
      } else {
        setError('Bir hata oluştu. Lütfen tekrar deneyiniz.');
      }
    } catch (err) {
      setError('Bir hata oluştu. Lütfen tekrar deneyiniz.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col relative overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50" />
        
        {/* Animated gradient mesh */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob" />
          <div className="absolute top-0 -right-4 w-72 h-72 bg-yellow-300 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000" />
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-4000" />
          <div className="absolute bottom-0 right-20 w-72 h-72 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-70 animate-blob animation-delay-2000" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob animation-delay-4000" />
        </div>
        
        {/* Subtle floating particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-gray-400 rounded-full opacity-20"
              style={{
                left: `${20 + i * 15}%`,
                top: `${10 + i * 10}%`,
                animation: `float ${15 + i * 2}s ease-in-out infinite`,
                animationDelay: `${i * 2}s`
              }}
            />
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          {/* Logo and Title */}
          <div className="text-center">
            <div className="flex justify-center mb-8">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-blue-500 rounded-3xl blur-2xl opacity-20" />
                <img 
                  src="/tahta-icon-minimal.svg" 
                  alt="Tahta" 
                  className="relative w-24 h-24 filter drop-shadow-lg"
                />
              </div>
            </div>
            <h1 className="text-4xl font-semibold text-gray-900 tracking-tight">
              Edesis Dijital Tahta
            </h1>
          </div>

          {/* Main Card with Glass Effect */}
          <div className="mt-8 relative">
            {/* Card shadow and blur effect */}
            <div className="absolute inset-0 bg-white/70 backdrop-blur-xl rounded-3xl shadow-xl" />
            
            {/* Card content */}
            <div className="relative bg-white/80 backdrop-blur-md rounded-3xl shadow-xl border border-gray-200/50 p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="orgCode" className="block text-sm font-medium text-gray-700 mb-2">
                    Kurum Kodu
                  </label>
                  <div className="relative">
                    <input
                      id="orgCode"
                      name="orgCode"
                      type="text"
                      autoComplete="off"
                      required
                      value={orgCode}
                      onChange={(e) => setOrgCode(e.target.value)}
                      onFocus={() => setFocused(true)}
                      onBlur={() => setFocused(false)}
                      className={`
                        block w-full px-4 py-3 
                        bg-gray-50/50 backdrop-blur-sm
                        border rounded-2xl
                        text-gray-900 placeholder-gray-400
                        transition-all duration-200
                        ${focused 
                          ? 'border-purple-400 ring-4 ring-purple-400/20' 
                          : 'border-gray-200 hover:border-gray-300'
                        }
                        focus:outline-none
                      `}
                      placeholder="örn: edesis"
                    />
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Uzantı eklemenize gerek yoktur (örn: .sinavza.com)
                  </p>
                </div>

                {error && (
                  <div className="rounded-2xl bg-red-50 border border-red-200 p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-800">{error}</p>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className={`
                    relative w-full
                    px-6 py-3
                    bg-gradient-to-r from-purple-600 to-blue-600
                    text-white font-medium
                    rounded-2xl
                    transition-all duration-200
                    hover:shadow-lg hover:shadow-purple-500/25
                    active:scale-[0.98]
                    disabled:opacity-50 disabled:cursor-not-allowed
                    disabled:hover:shadow-none
                    group
                  `}
                >
                  <span className="flex items-center justify-center gap-2">
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        <span>Kontrol ediliyor...</span>
                      </>
                    ) : (
                      <>
                        <span>Devam Et</span>
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
                      </>
                    )}
                  </span>
                </button>
              </form>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-8 text-center space-y-4">
            <div className="flex items-center justify-center gap-6 text-sm">
              <a
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-gray-900 transition-colors"
              >
                Gizlilik
              </a>
              <span className="text-gray-300">•</span>
              <a
                href="/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-gray-900 transition-colors"
              >
                Koşullar
              </a>
            </div>
            <p className="text-xs text-gray-400">
              © 2025 Edesis Technology. Tüm hakları saklıdır.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};